/**
 * Translating the web app's combatants into the shape an adapter understands.
 *
 * WHY THIS EXISTS. `useBattleSim` used to carry its own copy of 5e: a
 * substring scan of the equipment list that recognised eight weapons, a crit
 * rule that doubled the ability modifier, and a hit comparison written inline
 * at three call sites. The adapter has all of that properly now (ISS-0151
 * items 1-3), but it speaks `SystemCombatant` and the app speaks `Mob` and
 * `Character`. Something has to translate, and that something is here rather
 * than in the sim so it can be tested without React, five Zustand stores, or a
 * Supabase client.
 *
 * WHAT "PURE" BUYS. Nothing in this module reads a store. Every input arrives
 * as an argument, so a test can hand it a bestiary-shaped monster and a
 * character with a +1 Rapier and check what the adapter makes of them. The one
 * exception is the default `systemId`, which reads the registry — a global, not
 * a store, and every function takes it as an explicit argument when a caller
 * wants to pin it.
 *
 * WHAT DOES *NOT* BELONG HERE. Anything that decides what happens: which
 * weapon a character swings, whether a hit lands, how much it hurts. Those are
 * the system's, and the whole point of this file is that the sim no longer has
 * an opinion about them.
 */

import { armorClassOf, classesOf, getActiveSystemId, parseAffinityList, totalLevel } from '@machina/core';
import type {
  Character,
  CombatAction,
  DamageAffinity,
  MagicState,
  SystemCharacter,
  SystemCombatant,
} from '@machina/core';

import type { Mob, MobAction } from '../stores/mobStore';

/** The registry's active system, or a placeholder when nothing is active. */
function activeSystemId(): string {
  return getActiveSystemId() ?? 'unknown';
}

/**
 * A monster's three affinity strings, in the structured form core reads.
 *
 * `Mob` stores them comma-joined because that is what the SRD import produced
 * and what `dbToMob` makes of the DB's `string[]` columns. `parseAffinityList`
 * takes either shape and does exact-token matching, so
 * "bludgeoning, piercing, and slashing from nonmagical attacks" becomes three
 * qualified entries rather than a phrase that `.includes()` matched on anything.
 */
export function mobDamageAffinity(mob: Pick<Mob,
  'damageResistances' | 'damageImmunities' | 'damageVulnerabilities'>): DamageAffinity {
  return {
    resistant: parseAffinityList(mob.damageResistances),
    immune: parseAffinityList(mob.damageImmunities),
    vulnerable: parseAffinityList(mob.damageVulnerabilities),
  };
}

/**
 * A monster as the active system sees it.
 *
 * `type: 'creature'`, but still with a `SystemCharacter` envelope, because that
 * envelope is where the adapter looks for hit points, defence and conditions —
 * a creature without one is a combatant an adapter can roll against and never
 * hurt. The conditions matter more than they look: the 5e adapter reads them
 * for advantage and disadvantage, so a prone goblin is now actually easier to
 * hit, which the sim never modelled.
 */
export function mobToCombatant(mob: Mob, systemId: string = activeSystemId()): SystemCombatant {
  const name = mob.displayName || mob.name;

  const character: SystemCharacter = {
    id: mob.id,
    name,
    systemId,
    abilities: {
      strength: mob.strength,
      dexterity: mob.dexterity,
      constitution: mob.constitution,
      intelligence: mob.intelligence,
      wisdom: mob.wisdom,
      charisma: mob.charisma,
    },
    health: { current: mob.currentHp, max: mob.maxHp, temp: mob.tempHp },
    defense: mob.ac,
    speed: mob.speed,
    conditions: (mob.conditions ?? []).map(c => ({
      conditionId: c.type,
      ...(c.duration === undefined ? {} : { duration: c.duration }),
    })),
  };

  return {
    id: mob.id,
    name,
    type: 'creature',
    character,
    damageAffinity: mobDamageAffinity(mob),
  };
}

/**
 * The short ability ids an adapter keys its abilities by.
 *
 * `Character.spellcasting.ability` is a long name (`wisdom`) because the sheet
 * is written that way, and a system declares its abilities by id (`wis`). The
 * `abilities` map above ships long names and every adapter normalises them, but
 * `MagicState.ability` is not a map the adapter walks — it is a single key the
 * system looks its OWN abilities up by, so it goes over in the system's own
 * spelling. An ability with no short form travels unchanged rather than being
 * dropped: a system that keys abilities some third way still gets the string
 * the character declared.
 */
const ABILITY_SHORT_ID: Record<string, string> = {
  strength: 'str', dexterity: 'dex', constitution: 'con',
  intelligence: 'int', wisdom: 'wis', charisma: 'cha',
};

/**
 * A character's spellcasting as the `MagicState` contract describes it.
 *
 * `undefined` for a character with no `spellcasting` block, and that is the
 * whole point of the return type: a non-caster must arrive at the adapter with
 * no `magic` field at all, so `getSpellSaveDC` answers `undefined` and the
 * caller learns there is no DC rather than being handed one.
 *
 * `ability` is the load-bearing key. Before it existed the battle sim hardcoded
 * WIS for every NPC caster, so a sorcerer and a cleric rolled the same save DC.
 * `features` travels as names only — the adapter matches "Agonizing Blast" by
 * name and has no use for its source or prose.
 *
 * The `& Record<string, unknown>` is the seam between the two halves of the
 * contract: `SystemCharacter.magic` is deliberately open, because a system's
 * magic state is its own business, while `MagicState` names the keys every
 * system agrees on. An interface has no implicit index signature, so the
 * intersection is what lets a documented shape sit in the open field.
 */
export function characterMagicState(
  char: Character,
): (MagicState & Record<string, unknown>) | undefined {
  const spellcasting = char.spellcasting;
  if (!spellcasting) return undefined;

  const ability = spellcasting.ability?.trim().toLowerCase();

  return {
    ...(ability ? { ability: ABILITY_SHORT_ID[ability] ?? ability } : {}),
    known: [...(spellcasting.knownSpells ?? [])],
    prepared: [...(spellcasting.preparedSpells ?? [])],
    features: (char.features ?? []).map(f => f.name).filter((n): n is string => typeof n === 'string'),
  };
}

/**
 * A player character as the active system sees it.
 *
 * `meta.equipment` is the load-bearing field: it is how the adapter finds the
 * weapon behind an attack that names none, and it is the whole reason the sim
 * can stop scanning the equipment list itself.
 *
 * TWO DELIBERATE NON-DEFAULTS.
 *
 * `defense` is `armorClassOf(char)` with no `?? 10`. That accessor returns
 * undefined on purpose for a system with no armour class, and 10 is a real AC —
 * writing it here would tell the adapter this character is unarmoured rather
 * than that the question does not apply. The adapter has its own `?? 10` at the
 * point where it needs a number to beat, which is where that default is
 * defensible.
 *
 * `level` is the character's TOTAL level, or unset when they have no classes.
 * Unset rather than 0, because the adapter reads `level ?? 1` to derive a
 * proficiency bonus and a literal 0 would sail past that and produce +1.
 *
 * `magic` follows the same rule and for the same reason: omitted entirely for a
 * non-caster, so the system is never asked to produce a spell save DC for a
 * fighter.
 */
export function characterToCombatant(
  char: Character,
  systemId: string = activeSystemId(),
): SystemCombatant {
  const level = totalLevel(char);
  const magic = characterMagicState(char);

  const character: SystemCharacter = {
    id: char.id,
    name: char.name,
    systemId,
    abilities: { ...char.abilityScores },
    ...(level > 0 ? { level } : {}),
    classes: classesOf(char).map(c => ({
      classId: c.name,
      level: c.level,
      ...(c.subclass ? { subclass: c.subclass } : {}),
    })),
    health: { current: char.currentHp, max: char.maxHp, temp: char.tempHp },
    defense: armorClassOf(char),
    speed: char.speed,
    ...(magic ? { magic } : {}),
    // `Character` carries no condition list yet — `concentratingOn` is the only
    // status it tracks, and that is not a condition an attack roll reads.
    conditions: [],
  };

  return {
    id: char.id,
    name: char.name,
    type: 'character',
    character,
    meta: { equipment: (char.equipment ?? []).filter((i): i is string => typeof i === 'string') },
  };
}

/**
 * A monster's stat-block action as a `CombatAction`.
 *
 * Everything is stated, nothing derived: a stat block is the authority on its
 * own numbers, and the adapter believes an explicit `attackBonus` and
 * `damageDice` over anything it could work out. That is what stops the sim's
 * bestiary from being re-statted by whichever system happens to be active.
 */
export function mobActionToCombatAction(action: MobAction): CombatAction {
  return {
    name: action.name,
    attackBonus: action.attackBonus,
    damageDice: action.damageDice,
    damageType: action.damageType,
    range: action.range,
  };
}

/**
 * A weapon attack with nothing stated, so the system picks the weapon.
 *
 * This is what replaced `getCharacterAttacks`. The sim no longer knows what a
 * rapier is; it says "this character attacks" and the adapter reaches into
 * `meta.equipment`, finds the weapon, and applies finesse, versatile and the
 * magic bonus on its own.
 */
export function weaponAttackAction(name = 'Attack'): CombatAction {
  return { name };
}
